<template>
  <div class="card" v-if="showIndexDetail">
    <h5 class="card-header bg-primary text-white">Risk Index Finding Summary</h5>
    <div class="card-body p-0">
      <div class="container-fluid">
        <div class="row m-3" v-if="testTypeHasObservationQuestions && hasObservationQuestions">
          Note: This interview may contain findings about the interviewee's workplace observations that require follow up.
        </div>
        <div class="row m-3" v-else-if="testTypeHasObservationQuestions && !hasObservationQuestions">
          <CategoryIcon class="px-3 py-1" :categoryCode="'unknown'" />
          <span class="pt-2">The test taker did not report any observations of counterproductive workplace behavior.</span>
        </div>
        <div class="row m-3" v-if="!testTypeHasObservationQuestions">
          <p>
            Findings for Observations are available only on the Current Employee Integrity Assurance interview module. To set up this functionality, please
            contact us.
          </p>
          <p>
            The Current Employee Integrity Assurance interview is build for a wide range of programs, including code of conduct & ethics compliance, insider
            threat, and job promotion evaluations.
          </p>
        </div>
        <div class="row" v-if="testTypeHasObservationQuestions">
          <div class="col-12 col-md-4 px-0 border-right bg-light d-print-none">
            <table class="table table-hover border-bottom bg-white">
              <tbody>
                <tr
                  v-for="category in categories"
                  :key="category.code"
                  :class="{ 'table-active': selectedCategory == category, 'text-muted': isCategoryDisabled(category) }"
                  @click="selectedCategoryCode = category.code"
                >
                  <td class="p-0 position-relative">
                    <div class="d-flex justify-content-center border-0">
                      <div :class="getBgClass(category)" class="position-absolute" style="top: 0px; left: 0px; width: 16px; height: 16px; line-height: 12px;">
                        <fa :icon="getIcon(category)" fixed-width size="xs" />
                      </div>
                      <CategoryIcon class="px-3 py-1" :categoryCode="category.code" />
                    </div>
                  </td>
                  <td>{{ category.displayName }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="selectedCategory" class="col">
            <div class="row mt-2">
              <div class="mt-2 ml-3">
                <CategoryIcon :categoryCode="selectedCategory.code" />
              </div>
              <div class="col">
                <h4>{{ selectedCategory.displayName }}</h4>
              </div>
              <div>
                <Comments
                  :isLoading="isLoading"
                  :organizationId="organizationId"
                  :reportId="reportId"
                  :report="report"
                  subjectType="category"
                  :subjectId="selectedCategory.code"
                  @reload="$emit('reload')"
                  @error="$emit('error')"
                />
              </div>
            </div>
            <div class="row d-print-none">
              <div class="col">
                <div class="card mt-4" v-if="!hasQuestionsToViewInCategory">
                  <div class="card-body text-muted text-center" v-if="testTypeHasObservationQuestions">
                    The system analytics and the interviewee's responses did not identify observations of counterproductive workplace behaviors related to
                    {{ selectedCategory.displayName }}.
                  </div>
                </div>
                <div class="card mt-3" v-for="response in questionsToView" :key="response.responseId">
                  <div class="card-body py-3">
                    <div class="row">
                      <div class="mt-1 ml-3">
                        <fa :icon="getResponseIcon(response)" size="lg" :class="getResponseIconClass(response)" />
                      </div>
                      <div class="col pb-2">
                        <div class="row">
                          <div class="col">
                            <p class="font-weight-bold mb-2">{{ response.text }}</p>
                          </div>
                        </div>
                        <div class="row">
                          <div class="col text-capitalize">
                            Response: <span class="font-weight-bolder">{{ getResponseDisplayName(response) }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row" v-if="response.evasionCommentary">
                      <div class="col">
                        <div class="alert alert-warning m-0 py-2">
                          {{ response.evasionCommentary }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="card-footer py-2">
                    <div class="row">
                      <div class="col">
                        <form class="form">
                          <div class="form-group row mb-0">
                            <label class="col-form-label ml-3">Edit Response</label>
                            <div class="col" data-bs-toggle="tooltip" data-bs-delay="500" title="Update the test taker's response">
                              <input v-if="!canEditAnswer" type="text" readonly class="form-control-plaintext" :value="getResponseEditDescription(response)" />
                              <select
                                class="form-control"
                                v-if="canEditAnswer"
                                v-model="response.editorCode"
                                :disabled="isLoading"
                                @change="onResponseEdit(response)"
                              >
                                <option v-for="reportResponseEdit in reportEditOptions" :key="reportResponseEdit.code" :value="reportResponseEdit.code">{{
                                  reportResponseEdit.displayName
                                }}</option>
                              </select>
                            </div>
                          </div>
                        </form>
                      </div>
                      <div>
                        <Comments
                          :isLoading="isLoading"
                          :organizationId="organizationId"
                          :reportId="reportId"
                          :report="report"
                          :showText="false"
                          subjectType="response"
                          :subjectId="response.responseId"
                          @reload="$emit('reload')"
                          @error="$emit('error')"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="row d-none d-print-inline">
              <div class="col px-0">
                <div class="card mt-4" v-if="!hasQuestionsToViewInCategory">
                  <div class="card-body text-muted text-center" v-if="testTypeHasObservationQuestions">
                    The system analytics and the examinee's responses did not identify observations of counterproductive workplace behaviors related to
                    {{ selectedCategory.displayName }}.
                  </div>
                </div>
                <table class="table mt-1" v-if="hasQuestionsToViewInCategory">
                  <thead>
                    <tr>
                      <th></th>
                      <th class="text-left">Question</th>
                      <th class="text-center">Response</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="response in questionsToView" :key="response.responseId">
                      <td><fa :icon="getResponseIcon(response)" size="lg" :class="getResponseIconClass(response)" /></td>
                      <td class="text-left">
                        <span>{{ response.text }}</span>
                        <div class="alert alert-warning mt-2 mb-0 p-1" v-if="response.evasionCommentary">{{ response.evasionCommentary }}</div>
                        <div class="alert alert-secondary mt-2 mb-0 p-1" v-if="response.editorCode">{{ getResponseEditDescription(response) }}</div>
                      </td>
                      <td class="text-center text-capitalize">{{ getResponseDisplayName(response) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import CategoryIcon from "@/views/reports/report/edit-report/CategoryIcon.vue";
import Comments from "@/views/reports/report/edit-report/Comments.vue";

export default {
  components: {
    CategoryIcon,
    Comments,
  },
  props: {
    isLoading: Boolean,
    organizationId: String,
    reportId: String,
    report: Object,
  },
  data() {
    return {
      selectedCategoryCode: null,
    };
  },
  computed: {
    ...mapGetters("role", ["isReportAnswerEditor", "isInvestigationCreator", "isInvestigationViewer"]),
    ...mapGetters("static", ["reportResponseEdits", "reportObservationEdits"]),
    canEditAnswer() {
      return this.isReportAnswerEditor || this.isInvestigationCreator || this.isInvestigationViewer;
    },
    showIndexDetail() {
      return this.report != null && this.report.statusCode === "completed";
    },
    testTypeHasObservationQuestions() {
      if (this.report.registration.testCode == "periodic") return true;
      if (this.report.registration.testCode == "investigation") return true;
      return false;
    },
    riskIndex() {
      return this.report ? this.report.riskIndex || {} : {};
    },
    categories() {
      var categories = this.report ? (this.report.riskIndex ? this.report.riskIndex.categories : []) : [];

      var sortedCategories = categories.slice().filter(c => {
        // only show categories that contain Group 5 questions
        var questions = c.questions;
        for (var i = 0; i < questions.length; i++) {
          if (questions[i].groupCode == "5") return true;
        }
        return false;
      });

      sortedCategories.sort((x, y) => {
        if (this.sortProperty === "score") {
          var xMinScore = Math.min(x.originalScore, x.currentScore);
          var yMinScore = Math.min(y.originalScore, y.currentScore);
          if (xMinScore < yMinScore) return -1;
          if (xMinScore > yMinScore) return 1;
        }
        if (x.displayName < y.displayName) return -1;
        if (x.displayName > y.displayName) return 1;
        return 0;
      });

      sortedCategories.forEach(category => {
        category.questions.sort((x, y) => {
          var xSort = x.responseDisplayName !== x.preferredResponseDisplayName ? 1 : x.evasionCommentary ? 2 : 3;
          var ySort = y.responseDisplayName !== y.preferredResponseDisplayName ? 1 : y.evasionCommentary ? 2 : 3;
          if (xSort < ySort) return -1;
          if (xSort > ySort) return 1;
          return 0;
        });
      });

      return sortedCategories;
    },
    selectedCategory() {
      if (!this.categories || !this.categories.length) return null;
      return this.categories.find(c => c.code === this.selectedCategoryCode) ?? this.categories[0];
    },
    hasQuestionsToViewInCategory() {
      return this.questionsToView.length;
    },
    questionsToView() {
      return this.selectedCategory.questions.filter(q => q.groupCode == "5");
    },
    hasObservationQuestions() {
      var categories = this.report ? (this.report.riskIndex ? this.report.riskIndex.categories : []) : [];
      for (var i = 0; i < categories.length; i++) {
        var category = categories[i];
        if (category == null || category.questions == null) continue;
        for (var j = 0; j < category.questions.length; j++) {
          var question = category.questions[j];
          if (question.groupCode === "5") {
            return true;
          }
        }
      }
      return false;
    },
    reportEditOptions() {
      return this.reportObservationEdits;
    },
  },
  watch: {
    selectedCategory() {
      if (this.categories && this.categories.length > 0 && !this.selectedCategory) this.selectedCategoryCode = this.categories[0].code;
    },
  },
  created() {
    this.selectedCategoryCode = this.$route.query.cc;
  },
  methods: {
    getIcon(category) {
      if (!category.currentScore) return "circle";
      var hasNonPreferred = false;
      var questions = category.questions;
      for (var i = 0; i < questions.length; i++) {
        var response = questions[i];
        if (response.groupCode !== "5") continue;

        if (response.responseDisplayName !== response.preferredResponseDisplayName) {
          hasNonPreferred = true;
          break;
        }
      }
      if (hasNonPreferred) return "times";
      return "question";
    },
    getBgClass(category) {
      if (!category) return ["bg-secondary", "text-white"];
      if (this.isCategoryDisabled(category)) return ["bg-secondary", "text-white"];

      // any non-preferred gets danger, if all evasion gets warning
      var hasNonPreferred = false;
      var questions = category.questions;
      for (var i = 0; i < questions.length; i++) {
        var response = questions[i];
        if (response.groupCode !== "5") continue;

        if (response.responseDisplayName !== response.preferredResponseDisplayName) {
          hasNonPreferred = true;
          break;
        }
      }
      if (hasNonPreferred) return ["bg-danger", "text-white"];
      return ["bg-warning", "text-white"];
    },
    getOriginalBgClass(category) {
      if (!category) return ["bg-secondary", "text-white"];
      if (this.isCategoryDisabled(category)) return ["bg-secondary", "text-white"];
      if (category.originalScore >= 70) return ["bg-success", "text-white"];
      if (category.originalScore >= 50) return ["bg-warning", "text-white"];
      return ["bg-danger", "text-white"];
    },
    getResponseIcon(response) {
      if (response.evasionCommentary) return "exclamation";
      if (response.responseDisplayName !== response.preferredResponseDisplayName) return "exclamation";
      return "check";
    },
    getResponseIconClass(response) {
      if (response.evasionCommentary) return "text-warning";
      if (response.responseDisplayName !== response.preferredResponseDisplayName) return "text-danger";
      return "text-success";
    },
    getResponseEditDescription(response) {
      var record = this.reportResponseEdits.find(c => c.code === response.editorCode);
      return record ? record.displayName : "---";
    },
    getResponseDisplayName(response) {
      return response.responseDisplayName.toLowerCase() == "timeout" ? "The examinee avoided answering this question" : response.responseDisplayName;
    },
    isCategoryDisabled(category) {
      return category.statusCode != "enabled";
    },
    onResponseEdit(response) {
      this.$axios
        .put(`/v1/organizations/${this.organizationId}/reports/${this.reportId}/responses/${response.responseId}`, { editorCode: response.editorCode })
        .then(() => this.$emit("reload"))
        .catch(err => this.$emit("error", err));
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/base.scss";

table {
  @extend .mb-0;

  tr {
    cursor: pointer;
  }

  th,
  td {
    @extend .text-center;
    @extend .align-middle;

    h4,
    h5 {
      @extend .mb-0;
    }
  }
}

.custom-control-label {
  cursor: pointer;
}
</style>
